/**
 * Custom error page. Copied almost entirely from Sentry's examples on GitHub:
 *  https://github.com/vercel/next.js/blob/canary/examples/with-sentry/pages/_error.js
 */
import * as Sentry from '@sentry/nextjs';
import { ErrorPage } from 'components/util/ErrorPage';
import { NextPageContext } from 'next';
import { withSecureHeaders } from 'next-secure-headers';
import NextErrorComponent from 'next/error';

const MyError = ({ hasGetInitialPropsRun, err }) => {
  if (!hasGetInitialPropsRun && err) {
    // getInitialProps is not called in case of
    // https://github.com/vercel/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    Sentry.captureException(err);
    // Flushing is not required in this case as it only happens on the client
  }

  return <ErrorPage />;
};

MyError.getInitialProps = async ({ res, err, ...rest }: NextPageContext) => {
  const errorInitialProps = await NextErrorComponent.getInitialProps({
    ...rest,
    res,
    err,
  });

  // Workaround for https://github.com/vercel/next.js/issues/8592, mark when
  // getInitialProps has run
  // @ts-ignore
  errorInitialProps.hasGetInitialPropsRun = true;

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (err) {
    Sentry.captureException(err);
  }

  return errorInitialProps;
};

export default withSecureHeaders({
  frameGuard: false,
  contentSecurityPolicy: {
    directives: {
      frameAncestors: undefined,
    },
  },
})(MyError);
